import React, { useCallback, useMemo, useState } from "react";
// import { $createCodeNode } from "@lexical/code";
import { INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
// import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode";
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  TextNode,
} from "lexical";
import { createPortal } from "react-dom";
import clsx from "clsx";
import {
  CodeIcon,
  CodeSimpleIcon,
  ListBulletsIcon,
  ListChecksIcon,
  ListNumbersIcon,
  MinusIcon,
  ParagraphIcon,
  QuotesIcon,
  TableIcon,
  TextHOneIcon,
  TextHThreeIcon,
  TextHTwoIcon,
} from "@phosphor-icons/react";
import { ICON_SIZE } from "@/config/constants";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode";
import { INSERT_TABLE_COMMAND } from "@lexical/table";
import { $createCodeNode } from "@lexical/code";
// import { InsertEquationDialog } from "./EquationsPlugin";

class ComponentPickerOption extends MenuOption {
  // What shows up in the editor
  title: string;
  // Icon for display
  icon?: React.ReactElement;
  // For extra searching.
  keywords: Array<string>;
  // TBD
  keyboardShortcut?: string;
  // What happens when you select this option
  onSelect: (queryString: string) => void;

  constructor(
    title: string,
    options: {
      icon?: React.ReactElement;
      keywords?: Array<string>;
      keyboardShortcut?: string;
      onSelect: (queryString: string) => void;
    }
  ) {
    super(title);
    this.title = title;
    this.keywords = options.keywords || [];
    this.icon = options.icon;
    this.keyboardShortcut = options.keyboardShortcut;
    this.onSelect = options.onSelect.bind(this);
  }
}

function ComponentPickerMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: ComponentPickerOption;
}) {
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={clsx("flex cursor-pointer items-center gap-2 px-2 py-1 text-sm", { "bg-content3": isSelected })}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={"typeahead-item-" + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {option.icon}
      <span className="text">{option.title}</span>
    </li>
  );
}

function getBaseOptions(editor: LexicalEditor) {
  return [
    new ComponentPickerOption("Paragraph", {
      icon: <ParagraphIcon size={ICON_SIZE.sm} />,
      keywords: ["normal", "paragraph", "p", "text"],
      onSelect: () =>
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createParagraphNode());
          }
        }),
    }),
    ...([1, 2, 3] as const).map(
      (n) =>
        new ComponentPickerOption(`Heading ${n}`, {
          icon:
            n === 1 ? (
              <TextHOneIcon size={ICON_SIZE.sm} />
            ) : n === 2 ? (
              <TextHTwoIcon size={ICON_SIZE.sm} />
            ) : (
              <TextHThreeIcon size={ICON_SIZE.sm} />
            ),
          keywords: ["heading", "header", `h${n}`],
          onSelect: () =>
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                $setBlocksType(selection, () => $createHeadingNode(`h${n}`));
              }
            }),
        })
    ),
    new ComponentPickerOption("Numbered List", {
      icon: <ListNumbersIcon size={ICON_SIZE.sm} />,
      keywords: ["numbered list", "ordered list", "ol"],
      onSelect: () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption("Bulleted List", {
      icon: <ListBulletsIcon size={ICON_SIZE.sm} />,
      keywords: ["bulleted list", "unordered list", "ul"],
      onSelect: () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption("Check List", {
      icon: <ListChecksIcon size={ICON_SIZE.sm} />,
      keywords: ["check list", "todo list"],
      onSelect: () => editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption("Divider", {
      icon: <MinusIcon size={ICON_SIZE.sm} />,
      keywords: ["horizontal rule", "divider", "hr"],
      onSelect: () => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined),
    }),
    new ComponentPickerOption("Code Highlight", {
      icon: <CodeSimpleIcon size={ICON_SIZE.sm} />,
      keywords: ["code"],
      onSelect: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code"),
    }),
    new ComponentPickerOption("Quote", {
      icon: <QuotesIcon size={ICON_SIZE.sm} />,
      keywords: ["block quote"],
      onSelect: () =>
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createQuoteNode());
          }
        }),
    }),

    new ComponentPickerOption("Table", {
      icon: <TableIcon size={ICON_SIZE.sm} />,
      keywords: ["table", "grid", "spreadsheet", "rows", "columns"],
      onSelect: () =>
        editor.dispatchCommand(INSERT_TABLE_COMMAND, {
          rows: "4",
          columns: "3",
          includeHeaders: { columns: false, rows: true },
        }),
    }),

    new ComponentPickerOption("Code Block", {
      icon: <CodeIcon size={ICON_SIZE.sm} />,
      keywords: ["javascript", "python", "js", "codeblock"],
      onSelect: () =>
        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            if (selection.isCollapsed()) {
              $setBlocksType(selection, () => $createCodeNode());
            } else {
              // Will this ever happen?
              const textContent = selection.getTextContent();
              const codeNode = $createCodeNode();
              selection.insertNodes([codeNode]);
              selection.insertRawText(textContent);
            }
          }
        }),
    }),

    // new ComponentPickerOption("Equation", {
    //   icon: <FunctionIcon size={ICON_SIZE.sm} />,
    //   keywords: ["equation", "math", "formula", "latex", "katex"],
    //   onSelect: () => {
    //     // This will be handled by the modal in the plugin
    //     const openDialog = (globalThis as unknown as Record<string, () => void>).__openEquationDialog;
    //     openDialog?.();
    //   },
    // }),
  ];
}

export function ComponentPickerMenuPlugin() {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  // const {
  //   isOpen: isEquationModalOpen,
  //   onOpen: onEquationModalOpen,
  //   onOpenChange: onEquationModalOpenChange,
  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch("/", {
    minLength: 0,
  });

  // // Expose the equation dialog opener to the global scope for the menu option
  // React.useEffect(() => {
  //   const globals = globalThis as unknown as Record<string, () => void>;
  //   globals.__openEquationDialog = onEquationModalOpen;
  //   return () => {
  //     delete globals.__openEquationDialog;
  //   };
  // }, [onEquationModalOpen]);

  const options = useMemo(() => {
    const baseOptions = getBaseOptions(editor);

    if (!queryString) {
      return baseOptions;
    }

    const regex = new RegExp(queryString, "i");

    return [
      ...baseOptions.filter(
        (option) => regex.test(option.title) || option.keywords.some((keyword) => regex.test(keyword))
      ),
    ];
  }, [editor, queryString]);

  const onSelectOption = useCallback(
    (
      selectedOption: ComponentPickerOption,
      nodeToRemove: TextNode | null,
      closeMenu: () => void,
      matchingString: string
    ) => {
      editor.update(() => {
        nodeToRemove?.remove();
        selectedOption.onSelect(matchingString);
        closeMenu();
      });
    },
    [editor]
  );

  return (
    <>
      <LexicalTypeaheadMenuPlugin<ComponentPickerOption>
        onQueryChange={setQueryString}
        onSelectOption={onSelectOption}
        triggerFn={checkForTriggerMatch}
        options={options}
        menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
          anchorElementRef.current && options.length
            ? createPortal(
                <div className="border-default-100 bg-content1 scrollbar-hide max-h-35 min-w-max overflow-auto rounded-lg border shadow-lg">
                  <ul>
                    {options.map((option, i: number) => (
                      <ComponentPickerMenuItem
                        index={i}
                        isSelected={selectedIndex === i}
                        onClick={() => {
                          setHighlightedIndex(i);
                          selectOptionAndCleanUp(option);
                        }}
                        onMouseEnter={() => {
                          setHighlightedIndex(i);
                        }}
                        key={option.key}
                        option={option}
                      />
                    ))}
                  </ul>
                </div>,
                anchorElementRef.current
              )
            : null
        }
      />

      {/* <DrawerAndModal
        id="equation-modal"
        title="Insert Equation"
        externalState={{
          isOpen: isEquationModalOpen,
          onOpenChange: onEquationModalOpenChange,
        }}
      >
        <InsertEquationDialog activeEditor={editor} onClose={() => onEquationModalOpenChange()} />
      </DrawerAndModal> */}
    </>
  );
}
